import { forwardRef } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "info"
  | "warning"
  | "ghost";

export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and disables the button while truthy. */
  isLoading?: boolean;
  leftIcon?: IconDefinition;
  rightIcon?: IconDefinition;
  fullWidth?: boolean;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: "bg-theme-primary hover:bg-theme-primary-hover text-theme-primary-contrast",
  secondary: "bg-theme-surface-2 hover:bg-theme-surface text-theme-text border border-theme-border",
  success: "bg-theme-success hover:bg-theme-success-hover text-white",
  danger: "bg-theme-danger hover:bg-theme-danger-hover text-white",
  info: "bg-theme-info hover:bg-theme-info-hover text-white",
  warning: "bg-theme-warning hover:bg-theme-warning-hover text-theme-primary-contrast",
  ghost: "bg-transparent hover:bg-theme-surface-2 text-theme-text",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "px-3 py-1 text-sm",
  md: "px-4 py-2",
  lg: "px-6 py-3 text-lg",
};

/**
 * Shared button. Prefer this over raw <button> + ad-hoc Tailwind so styling stays
 * consistent across the app. Accepts all native button attributes.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      type = "button",
      disabled,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        aria-busy={isLoading}
        className={clsx(
          "relative inline-flex items-center justify-center rounded-sm font-bold transition cursor-pointer whitespace-nowrap",
          // While loading the button keeps its full colour (only the label is swapped
          // for a spinner); the dimmed look is reserved for the genuinely-disabled state.
          isLoading ? "cursor-wait" : "disabled:opacity-50 disabled:cursor-not-allowed",
          VARIANT_CLASSES[variant],
          SIZE_CLASSES[size],
          fullWidth && "w-full",
          className
        )}
        {...rest}
      >
        {isLoading && (
          <span className="absolute inset-0 flex items-center justify-center animate-fade-in">
            <FontAwesomeIcon icon={faSpinner} spin />
          </span>
        )}
        {/* Content stays mounted (so the button keeps its width) but is hidden while loading. */}
        <span className={clsx("inline-flex items-center gap-2", isLoading && "invisible")}>
          {leftIcon && <FontAwesomeIcon icon={leftIcon} />}
          {children}
          {rightIcon && <FontAwesomeIcon icon={rightIcon} />}
        </span>
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
